import React, { useState } from 'react';

const FeedbackModal = ({ isOpen, onClose, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onSubmit({ rating, feedback });
      setRating(0);
      setFeedback('');
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setFeedback('');
    onClose();
  };

  // Function to get emoji based on rating
  const getRatingEmoji = (ratingValue) => {
    switch(ratingValue) {
      case 1: return '😠';
      case 2: return '😕';
      case 3: return '😐';
      case 4: return '🙂';
      case 5: return '😍';
      default: return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-indigo-800 text-white p-6 flex justify-between items-center">
          <h2 className="text-xl font-bold">Share Your Feedback</h2>
          <button 
            onClick={handleClose}
            className="text-white hover:text-indigo-200 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-6 text-center">
            <p className="text-gray-700 mb-4">How would you rate your experience?</p>
            
            {/* Emoji display based on rating */}
            <div className="text-5xl mb-4">
              {getRatingEmoji(hoverRating || rating)}
            </div>
            
            <div className="flex justify-center space-x-3 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  className={`text-5xl ${star <= (hoverRating || rating) ? 'text-yellow-400' : 'text-gray-300'} transition-all transform hover:scale-110`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  aria-label={`Rate ${star} out of 5`}
                >
                  {star <= (hoverRating || rating) ? '★' : '☆'}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500">
              {rating === 0 ? 'Select your rating' : `You rated ${rating} star${rating > 1 ? 's' : ''}`}
            </p>
          </div>
          
          <div className="mb-6">
            <label htmlFor="feedback" className="block text-gray-700 mb-2 font-medium">
              Your Feedback & Suggestions
            </label>
            <textarea
              id="feedback"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Please share your thoughts and suggestions for improvement..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows="4"
            />
          </div>
          
          <div className="flex space-x-4">
            <button 
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              className="flex-1 px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;